import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import axiosInstance, { getHeaders } from "../../interceptor/axiosInstance";
import { Report } from "../../models/Report/report.model";
import { deserialize, serialize } from "serializr";
import Notification from "../../shared/components/Notification";
import { NotificationTypes } from "../../enums/notificationTypes";
import { DownloadFileTypeEnum } from "../../enums/downloadFileType.enum";
import { CaseDetail } from "../../models/CaseDetail/caseDetail.model";
import { formatFileNameFromCaseDetail } from "../../shared/utils/dataFormatConverter";
import { ReportCaseDetail } from "../../models/Assignment/assignment.model";
import { UserRoleEnum } from "../../enums/userRole.enum";
import { ReportTemplate } from "../../models/ReportTemplate/reportTemplate.model";

/**
 * Minimal case context the AI server understands. Kept structural (all optional)
 * so both {@link CaseDetail} and the lighter `ReportCaseDetail` satisfy it.
 */
export interface AiReportCaseContext {
  id?: number;
  scanTypeName?: string;
  currentClinicalProblem?: string;
  patientHistory?: string;
  parsedPatientHistory?: string;
  additionalDetails?: string;
  age?: string;
  gender?: string;
  contrast?: string;
  studyDescription?: string;
  caseBodyParts?: { bodyPartName?: string }[];
}

/** Optional payload for `/ai_reporting/ai/stream` (FastAPI). */
export interface GeminiReportAiOptions {
  adminId?: number;
  /** false = full report from findings only; true = merge findings into editor/template HTML. */
  useReportTemplate?: boolean;
  /** Case context; the service derives scan type, clinical indication and patient/study fields. */
  caseDetail?: AiReportCaseContext;
  /** Drives FastAPI `user_role` for session validation. Required by the AI server. */
  userRole?: string;
  /** Explicit prior report HTML for refinement; never auto-derived from the editor. */
  priorReport?: string;
  /** Called with accumulated HTML as each model chunk arrives (streaming endpoint). */
  onStreamChunk?: (accumulatedHtml: string) => void;
}

function parseFastApiDetailBody(data: any): string {
  if (data == null) return "Request failed";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail
      .map(
        (e: { msg?: string; loc?: unknown }) =>
          `${e?.loc != null ? JSON.stringify(e.loc) : ""} ${e?.msg ?? ""}`.trim()
      )
      .filter(Boolean)
      .join("; ");
  }
  if (data.detail != null && typeof data.detail === "object") {
    return JSON.stringify(data.detail);
  }
  return "Smart Report generation failed";
}

/** A single turn in the RadAI Assistant conversation. */
export interface AiChatMessage {
  role: "user" | "assistant";
  content: string;
}

/** Options for the RadAI Assistant chat stream (`/ai_reporting/chat/stream`). */
export interface AiChatOptions {
  /** Drives FastAPI `user_role` for session validation. Required. */
  userRole?: string;
  /** Current report editor HTML — sent as grounding context for the assistant. */
  reportContent?: string;
  /** Case context; the service derives scan type, clinical indication, patient/study fields. */
  caseDetail?: AiReportCaseContext;
}

/** Maps signed-in user to tenant admin id for per-admin prompts on the AI server. */
export function resolveAiReportingAdminId(
  userRole: string | undefined,
  user: { id?: number; adminId?: number | string } | undefined
): number | undefined {
  if (!user) return undefined;
  if (userRole === UserRoleEnum.ADMIN) {
    return typeof user.id === "number" ? user.id : undefined;
  }
  const raw = user.adminId;
  if (raw === undefined || raw === null || raw === "") return undefined;
  const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Provider used for all AI reporting in staging-ai-plugin.
 *
 * NOTE: Gemini is the intended default, but the Gemini API project's prepaid
 * credits are currently depleted (429 RESOURCE_EXHAUSTED), so we temporarily
 * route to OpenAI. Flip this back to "gemini" once Gemini billing is restored.
 * (The server must have the matching provider key configured, e.g.
 * OPENAI_API_KEY for "openai".)
 */
export const AI_REPORTING_PROVIDER: "gemini" | "openai" | "claude" = "openai";

/** Kept as a function so callers/imports stay stable. */
export function resolveAiReportingLlmProvider(): "gemini" | "openai" | "claude" {
  return AI_REPORTING_PROVIDER;
}

/** FastAPI `user_role` for Rails session validation (excludes referring_doctor). */
function railsUserRoleForFastApi(
  userRole: string | undefined
): "admin" | "doctor" | "scan_center" | undefined {
  if (userRole === UserRoleEnum.ADMIN) return "admin";
  if (userRole === UserRoleEnum.DOCTOR) return "doctor";
  if (userRole === UserRoleEnum.SCAN_CENTER) return "scan_center";
  return undefined;
}

const INVALID_AI_FIELD_VALUES = new Set([
  "", "-", "--", "n/a", "na", "none", "null", "nil",
  "not provided", "not set", "not specified", "not available",
  "unknown", "0", "invalid", "undefined",
]);

/** Returns a trimmed value only when it is meaningful (not a placeholder sentinel). */
function validAiField(value: unknown): string | undefined {
  if (value == null) return undefined;
  const v = String(value).trim();
  return INVALID_AI_FIELD_VALUES.has(v.toLowerCase()) ? undefined : v || undefined;
}

/** Combine the case clinical fields into a single clinical indication string. */
function clinicalIndicationFromCase(caseDetail?: AiReportCaseContext): string {
  if (!caseDetail) return "Not provided";
  const parts = [
    caseDetail.currentClinicalProblem,
    caseDetail.patientHistory,
    caseDetail.additionalDetails,
  ]
    .map((s) => (s || "").trim())
    .filter(Boolean);
  const combined = parts.join(" ").trim();
  return combined || "Not provided";
}

export default class ReportService {
  static fetchReports(
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    axiosInstance
      .get(ApiRoutes.REPORTS)
      .then((response) => {
        const reports = deserialize(Report, response.data["reports"]);
        onSuccess(reports);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static fetchDirectReports(
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    axiosInstance
      .get(ApiRoutes.DIRECT_REPORTS)
      .then((response) => {
        const reports = deserialize(Report, response.data);
        onSuccess(reports);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }


  static showReport(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + reportId;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        if (response.data.report) {
          const report = deserialize(Report, response.data["report"]);
          onSuccess(report);
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showDirectReport(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.DIRECT_REPORTS + "/" + reportId;
    axiosInstance
      .get(API_URL)
      .then((response) => {
        if (response.data) {
          const report = deserialize(Report, response.data);
          onSuccess(report);
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static showAdminReport(
    caseDetailId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.ADMIN_REPORTS.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL)
      .then((response) => {
        if (response.data.report) {
          const report = deserialize(Report, response.data["report"]);
          onSuccess(report);
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static createReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    axiosInstance
      .post(ApiRoutes.REPORTS, reportJSON)
      .then((response) => {
        Notification({
          message: "Report created",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static updateReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + report.id;
    const reportJSON = { report: serialize(report) };
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report updated",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static publishReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    let API_URL = ApiRoutes.PUBLISH_REPORT;
    if (report?.id) {
      API_URL = API_URL.replace(":reportId", report.id.toString());
    }
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report published",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static approveAndPublishReport(
    report: Report,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    let API_URL = ApiRoutes.APPROVE_AND_PUBLISH_REPORT;
    if (report?.id) {
      API_URL = API_URL.replace(":reportId", report.id.toString());
    }
    axiosInstance
      .put(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report approved and published",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static sendForApproval(
    report: Report,
    caseDetailId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report: serialize(report) };
    const API_URL = ApiRoutes.SEND_FOR_APPROVAL.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .post(API_URL, reportJSON)
      .then((response) => {
        Notification({
          message: "Report sent for approval",
          type: NotificationTypes.SUCCESS,
        });
        const report = deserialize(Report, response.data["report"]);
        onSuccess(report);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static downloadReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: ReportCaseDetail,
    withLetterHead: boolean,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetail?.id) {
      let API_URL = ApiRoutes.DOWNLOAD_PDF.replace(
          ":caseDetailId",
          caseDetail?.id.toString()
      ) + '?report_id=' + reportId;
      let api = axiosInstance.get(API_URL);
      if (fileType === DownloadFileTypeEnum.WORD) {
        API_URL = ApiRoutes.DOWNLOAD_WORD.replace(
          ":caseDetailId",
          caseDetail?.id.toString()
        ) + '?report_id=' + reportId;
        api = axiosInstance.get(API_URL, { responseType: "blob" });
      }
      api
        .then((response) => {
          if (fileType === DownloadFileTypeEnum.WORD) {
            const blob = new Blob([response.data], {
              type:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadURL;
            link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
            link.click();
          } else {
            onSuccess(
              withLetterHead
                ? response.data.letter_head_report
                : response.data.report
            );
          }
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }

  static downloadApprovalReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: CaseDetail,
    withLetterHead: boolean,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    if (caseDetail?.id) {
      let API_URL = ApiRoutes.APPROVAL_DOWNLOAD_PDF.replace(
        ":caseDetailId",
        caseDetail?.id.toString()
      );
      let api = axiosInstance.get(API_URL);
      if (fileType === DownloadFileTypeEnum.WORD) {
        API_URL = ApiRoutes.APPROVAL_DOWNLOAD_WORD.replace(
          ":caseDetailId",
          caseDetail?.id.toString()
        );
        api = axiosInstance.get(API_URL, { responseType: "blob" });
      }
      api
        .then((response) => {
          if (fileType === DownloadFileTypeEnum.WORD) {
            const blob = new Blob([response.data], {
              type:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const downloadURL = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadURL;
            link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
            link.click();
          } else {
            onSuccess(
              withLetterHead
                ? response.data.letter_head_report
                : response.data.report
            );
          }
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
    }
  }

  static downloadScanCenterReport(
    fileType: DownloadFileTypeEnum,
    caseDetail: CaseDetail,
    withLetterHead: boolean,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const caseDetailId = caseDetail?.id ?? 0;
    let API_URL = ApiRoutes.SCAN_CENTER_DOWNLOAD_PDF.replace(
      ":caseDetailId",
      caseDetailId.toString()
    ) + '?report_id=' + reportId;
    let api = axiosInstance.get(API_URL);
    if (fileType === DownloadFileTypeEnum.WORD) {
      API_URL = ApiRoutes.SCAN_CENTER_DOWNLOAD_WORD.replace(
        ":caseDetailId",
        caseDetailId.toString()
      ) + '?report_id=' + reportId;
      api = axiosInstance.get(API_URL, { responseType: "blob" });
    }
    api
      .then((response) => {
        if (fileType === DownloadFileTypeEnum.WORD) {
          const blob = new Blob([response.data], {
            type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadURL;
          link.download = formatFileNameFromCaseDetail(caseDetail) + ".docx";
          link.click();
        } else {
          onSuccess(
            withLetterHead
              ? response.data.letter_head_report
              : response.data.report
          );
        }
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static previewScanCenterReport(
      caseDetailId: number,
      reportId: number,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.SCAN_CENTER_PREVIEW_REPORT.replace(
        ":caseDetailId",
        caseDetailId.toString()
    );
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
          responseType: "blob",
        })
        .then((response) => {
          onSuccess(response.data);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static previewReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.PREVIEW_REPORT.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL, {
        params: reportJSON,
        responseType: "blob",
      })
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static previewApprovalReport(
    caseDetailId: number,
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const reportJSON = { report_id: reportId };
    let API_URL = ApiRoutes.APPROVAL_PREVIEW_REPORT.replace(
      ":caseDetailId",
      caseDetailId.toString()
    );
    axiosInstance
      .get(API_URL, {
        params: reportJSON,
        responseType: "blob",
      })
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static deleteReport(
    reportId: number,
    onSuccess: Function,
    onError: Function,
    onFinal: () => void
  ) {
    const API_URL = ApiRoutes.REPORTS + "/" + reportId;
    axiosInstance
      .delete(API_URL)
      .then((response) => {
        onSuccess();
      })
      .catch((error) => {
        onError();
      })
      .finally(() => {
        onFinal();
      });
  }

  static getVoiceToTextInfo(
      user_type: string | undefined,
      user_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type };
    let API_URL = ApiRoutes.VOICE_TO_TEXT_INFO;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
        })
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static storeVoiceToTextSessionData(
      user_type: string | undefined,
      user_id: number | undefined,
      used_seconds: number | undefined,
      case_detail_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type, user_id: user_id, used_seconds: used_seconds, case_detail_id: case_detail_id };
    let API_URL = ApiRoutes.VOICE_TO_TEXT_SESSIONS;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getAiReportingInfo(
      user_type: string | undefined,
      user_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type };
    let API_URL = ApiRoutes.AI_REPORTING_INFO;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .get(API_URL, {
          params: reportJSON,
        })
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static storeAiReportingSessionData(
      user_type: string | undefined,
      user_id: number | undefined,
      used_credits: number | undefined,
      case_detail_id: number | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = { user_type: user_type, user_id: user_id, used_credits: 1, case_detail_id: case_detail_id };
    let API_URL = ApiRoutes.AI_REPORTING_SESSIONS;
    if (user_type && user_id) {
      API_URL = API_URL.replace(":userId", user_id.toString());
    }
    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static generateGeminiReport(
      report: Report,
      /** Current editor / template HTML (used as `text_content`; merged by the model in template mode). */
      editorTemplateHtml: string | undefined,
      /** Key findings / dictation — the primary model input (sent as `report_content`). */
      keyFindings: string | undefined,
      reportTemplates: ReportTemplate[] = [],
      onSuccess: Function,
      onError: Function,
      onFinal: () => void,
      aiOptions?: GeminiReportAiOptions
  ) {
    const fastApiBase = process.env.REACT_APP_FASTAPI_BASE_URL;
    if (!fastApiBase) {
      onError("AI reporting is not configured (REACT_APP_FASTAPI_BASE_URL).");
      onFinal();
      return;
    }

    const railsRole = railsUserRoleForFastApi(aiOptions?.userRole);
    if (!railsRole) {
      onError(
        "AI reporting requires an admin, doctor, or scan center session."
      );
      onFinal();
      return;
    }

    const caseDetail = aiOptions?.caseDetail;

    // staging-ai-plugin always merges the findings into the current report
    // template (refine-style), since a report template is mandatory. We treat
    // the live editor HTML as the template and let the AI merge into it so the
    // layout (tables, sections, headings) is preserved. Only fall back to
    // freeform when the editor genuinely has no template content (avoids the
    // backend's "template HTML is required" 400 in template mode).
    const editorHtml = editorTemplateHtml ?? "";
    const trimmedEditor = editorHtml.trim().toLowerCase();
    const TRIVIAL_EDITOR_HTML = new Set([
      "", ".", "<p></p>", "<p><br></p>", "<p><br/></p>", "<p>.</p>", "<p>&nbsp;</p>",
    ]);
    const editorHasTemplate = !TRIVIAL_EDITOR_HTML.has(trimmedEditor);
    const isTemplateMode = (aiOptions?.useReportTemplate ?? true) && editorHasTemplate;

    const bodyPartsStr = validAiField(
      caseDetail?.caseBodyParts
        ?.map((p) => p.bodyPartName)
        .filter(Boolean)
        .join(", ")
    );

    const priorReportHtml = (aiOptions?.priorReport ?? "").trim();

    const reportJSON: Record<string, unknown> = {
      // FastAPI contract: report_content = key findings, text_content = editor/template HTML.
      report_content: keyFindings ?? "",
      text_content: editorHtml,
      use_report_template: isTemplateMode,
      user_role: railsRole,
      // Provider toggle (currently OpenAI — Gemini billing credits depleted).
      llm_provider: AI_REPORTING_PROVIDER,
      scan_type_name: validAiField(caseDetail?.scanTypeName) || "Not provided",
      clinical_indication: clinicalIndicationFromCase(caseDetail),
      report_templates: reportTemplates.map((template) => ({
        id: template.id,
        name: template.name || "",
        content: template.content || "",
      })),
      // Patient / study context — only sent when valid (non-empty, non-sentinel).
      ...(validAiField(caseDetail?.age) ? { patient_age: validAiField(caseDetail?.age) } : {}),
      ...(validAiField(caseDetail?.gender) ? { patient_sex: validAiField(caseDetail?.gender) } : {}),
      ...(validAiField(caseDetail?.parsedPatientHistory) ? { patient_history: validAiField(caseDetail?.parsedPatientHistory) } : {}),
      ...(validAiField(caseDetail?.contrast) ? { contrast: validAiField(caseDetail?.contrast) } : {}),
      ...(validAiField(caseDetail?.studyDescription) ? { study_description: validAiField(caseDetail?.studyDescription) } : {}),
      ...(bodyPartsStr ? { body_parts: bodyPartsStr } : {}),
      // Refinement against an existing report — only when the caller opts in.
      ...(priorReportHtml ? { prior_report: priorReportHtml } : {}),
      ...(caseDetail?.id ? { case_detail_id: caseDetail.id } : {}),
    };
    if (aiOptions?.adminId !== undefined) {
      reportJSON.admin_id = aiOptions.adminId;
    }

    const streamUrl = fastApiBase + ApiRoutes.GENERATE_AI_REPORT_STREAM;

    const runStream = async () => {
      try {
        const res = await fetch(streamUrl, {
          method: "POST",
          headers: {
            ...getHeaders(),
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify(reportJSON),
        });
        if (res.status === 429) {
          const retry = res.headers.get("Retry-After");
          onError(
            retry
              ? `Too many requests. Try again in ${retry} seconds.`
              : "Too many AI requests. Please wait a minute and try again."
          );
          return;
        }
        if (!res.ok) {
          let detail = "Smart Report generation failed";
          try {
            const errBody = await res.json();
            const d = errBody?.detail;
            if (typeof d === "string") {
              detail = d;
            } else if (Array.isArray(d)) {
              detail = d
                .map(
                  (e: { msg?: string; loc?: unknown }) =>
                    `${e?.loc ? JSON.stringify(e.loc) : ""}: ${e?.msg ?? ""}`
                )
                .join("; ");
            }
          } catch {
            /* ignore */
          }
          onError(detail);
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          onError("Smart Report generation failed");
          return;
        }
        const decoder = new TextDecoder();
        let buffer = "";
        let acc = "";
        let sawDone = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          for (;;) {
            const sep = buffer.indexOf("\n\n");
            if (sep < 0) break;
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const dataLine = block.split("\n").find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            const raw = dataLine.replace(/^data:\s?/, "").trim();
            if (!raw) continue;
            let payload: {
              delta?: string;
              done?: boolean;
              generated_text?: string;
              error?: string;
            };
            try {
              payload = JSON.parse(raw);
            } catch {
              continue;
            }
            if (payload.error) {
              onError(String(payload.error));
              return;
            }
            if (payload.delta) {
              acc += payload.delta;
              aiOptions?.onStreamChunk?.(acc);
            }
            if (payload.done) {
              sawDone = true;
              Notification({
                message: "Radiolinq AI Assist Done!",
                type: NotificationTypes.SUCCESS,
              });
              const finalText =
                typeof payload.generated_text === "string"
                  ? payload.generated_text
                  : acc;
              onSuccess(finalText);
              return;
            }
          }
        }
        if (!sawDone) {
          onError("Smart Report generation failed");
        }
      } catch (e) {
        console.error("Error generating report:", e);
        onError("Smart Report generation failed");
      }
    };

    void runStream().finally(() => {
      onFinal();
    });
  }

  /**
   * RadAI Assistant — interactive multi-turn chat (streaming SSE).
   *
   * Sends the full conversation plus optional case context to
   * `/ai_reporting/chat/stream` and streams the assistant reply. Returns an
   * `abort()` handle so the caller can cancel an in-flight response.
   */
  static streamAiChat(
    messages: AiChatMessage[],
    options: AiChatOptions,
    callbacks: {
      onDelta: (accumulated: string) => void;
      onDone: (finalText: string) => void;
      onError: (message: string) => void;
    }
  ): { abort: () => void } {
    const noop = { abort: () => {} };
    const fastApiBase = process.env.REACT_APP_FASTAPI_BASE_URL;
    if (!fastApiBase) {
      callbacks.onError(
        "AI assistant is not configured (REACT_APP_FASTAPI_BASE_URL)."
      );
      return noop;
    }
    const railsRole = railsUserRoleForFastApi(options.userRole);
    if (!railsRole) {
      callbacks.onError(
        "AI assistant requires an admin, doctor, or scan center session."
      );
      return noop;
    }

    const caseDetail = options.caseDetail;
    const bodyPartsStr = validAiField(
      caseDetail?.caseBodyParts
        ?.map((p) => p.bodyPartName)
        .filter(Boolean)
        .join(", ")
    );

    const reportJSON: Record<string, unknown> = {
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
      user_role: railsRole,
      llm_provider: AI_REPORTING_PROVIDER,
      report_content: options.reportContent ?? "",
      clinical_indication: clinicalIndicationFromCase(caseDetail),
      ...(validAiField(caseDetail?.scanTypeName)
        ? { scan_type_name: validAiField(caseDetail?.scanTypeName) }
        : {}),
      ...(validAiField(caseDetail?.age)
        ? { patient_age: validAiField(caseDetail?.age) }
        : {}),
      ...(validAiField(caseDetail?.gender)
        ? { patient_sex: validAiField(caseDetail?.gender) }
        : {}),
      ...(validAiField(caseDetail?.parsedPatientHistory)
        ? { patient_history: validAiField(caseDetail?.parsedPatientHistory) }
        : {}),
      ...(validAiField(caseDetail?.studyDescription)
        ? { study_description: validAiField(caseDetail?.studyDescription) }
        : {}),
      ...(bodyPartsStr ? { body_parts: bodyPartsStr } : {}),
      ...(caseDetail?.id ? { case_detail_id: caseDetail.id } : {}),
    };

    const controller = new AbortController();
    const streamUrl = fastApiBase + ApiRoutes.AI_CHAT_STREAM;

    const run = async () => {
      try {
        const res = await fetch(streamUrl, {
          method: "POST",
          headers: {
            ...getHeaders(),
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify(reportJSON),
          signal: controller.signal,
        });
        if (res.status === 429) {
          const retry = res.headers.get("Retry-After");
          callbacks.onError(
            retry
              ? `Too many requests. Try again in ${retry} seconds.`
              : "Too many AI requests. Please wait a minute and try again."
          );
          return;
        }
        if (!res.ok) {
          let detail = "AI assistant request failed";
          try {
            const errBody = await res.json();
            const d = errBody?.detail;
            if (typeof d === "string") {
              detail = d;
            } else if (Array.isArray(d)) {
              detail = d
                .map(
                  (e: { msg?: string; loc?: unknown }) =>
                    `${e?.loc ? JSON.stringify(e.loc) : ""}: ${e?.msg ?? ""}`
                )
                .join("; ");
            }
          } catch {
            /* ignore */
          }
          callbacks.onError(detail);
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          callbacks.onError("AI assistant request failed");
          return;
        }
        const decoder = new TextDecoder();
        let buffer = "";
        let acc = "";
        let sawDone = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          for (;;) {
            const sep = buffer.indexOf("\n\n");
            if (sep < 0) break;
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const dataLine = block
              .split("\n")
              .find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            const raw = dataLine.replace(/^data:\s?/, "").trim();
            if (!raw) continue;
            let payload: {
              delta?: string;
              done?: boolean;
              generated_text?: string;
              error?: string;
            };
            try {
              payload = JSON.parse(raw);
            } catch {
              continue;
            }
            if (payload.error) {
              callbacks.onError(String(payload.error));
              return;
            }
            if (payload.delta) {
              acc += payload.delta;
              callbacks.onDelta(acc);
            }
            if (payload.done) {
              sawDone = true;
              const finalText =
                typeof payload.generated_text === "string"
                  ? payload.generated_text
                  : acc;
              callbacks.onDone(finalText);
              return;
            }
          }
        }
        if (!sawDone) {
          // Stream ended without an explicit done event — use what we have.
          if (acc) callbacks.onDone(acc);
          else callbacks.onError("AI assistant request failed");
        }
      } catch (e) {
        if ((e as { name?: string })?.name === "AbortError") return;
        console.error("AI assistant stream error:", e);
        callbacks.onError("AI assistant request failed");
      }
    };

    void run();
    return { abort: () => controller.abort() };
  }

  static generateUsgReport(
      case_detail_id: number | undefined,
      report: Report,
      textContent: string | undefined,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    const reportJSON = {
      report_content: textContent,
    };

    let API_URL = process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GENERATE_USG_REPORT;

    if (case_detail_id) {
      API_URL = API_URL.replace(":caseDetailId", case_detail_id.toString());
    }

    axiosInstance
        .post(API_URL, reportJSON)
        .then((response) => {
          Notification({
            message: "USG report generated successfully!",
            type: NotificationTypes.SUCCESS,
          });
          const report = response.data["generated_text"];
          onSuccess(report);
        })
        .catch((error) => {
          console.error("Error generating report:", error);
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getAiDiagnosisReport(
      caseDetailId: number,
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.AI_DIAGNOSIS_DATA + caseDetailId.toString())
        .then((response) => {
          const ai_report_template = response.data["ai_report_template"];
          const status = response.data["status"];
          onSuccess(ai_report_template, status);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static getOrganList(
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  ) {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GET_ORGANS_LIST)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }
  static getOrganPathologyDetails(
      onSuccess: Function,
      onError: Function,
      onFinal: () => void
  )
  {
    axiosInstance
        .get(process.env.REACT_APP_FASTAPI_BASE_URL! + ApiRoutes.GET_ORGANS_PATHOLOGY_DETAILS)
        .then((response) => {
          onSuccess(response);
        })
        .catch((error) => {
          onError();
        })
        .finally(() => {
          onFinal();
        });
  }

  static fetchAiReportingCopilot(
    userRole: string | undefined,
    onSuccess: (prompt: string) => void,
    onError: (message: string) => void
  ) {
    const fastApiBase = process.env.REACT_APP_FASTAPI_BASE_URL;
    if (!fastApiBase) {
      onError("AI reporting is not configured (REACT_APP_FASTAPI_BASE_URL).");
      return;
    }
    const rails = railsUserRoleForFastApi(userRole);
    if (!rails) {
      onError(
        "Copilot is only available for admin, doctor, or scan center users."
      );
      return;
    }
    const url = `${fastApiBase.replace(/\/$/, "")}${
      ApiRoutes.AI_REPORTING_COPILOT
    }?user_role=${encodeURIComponent(rails)}`;
    void fetch(url, { method: "GET", headers: getHeaders() })
      .then(async (res) => {
        if (!res.ok) {
          let detail = "Could not load copilot";
          try {
            const errBody = await res.json();
            detail = parseFastApiDetailBody(errBody);
          } catch {
            /* ignore */
          }
          onError(detail);
          return;
        }
        const data = (await res.json()) as { prompt?: string };
        onSuccess(data.prompt ?? "");
      })
      .catch(() => onError("Could not load copilot"));
  }

  static saveAiReportingCopilot(
    userRole: string | undefined,
    prompt: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) {
    const fastApiBase = process.env.REACT_APP_FASTAPI_BASE_URL;
    if (!fastApiBase) {
      onError("AI reporting is not configured (REACT_APP_FASTAPI_BASE_URL).");
      return;
    }
    const rails = railsUserRoleForFastApi(userRole);
    if (!rails) {
      onError(
        "Copilot is only available for admin, doctor, or scan center users."
      );
      return;
    }
    const url = `${fastApiBase.replace(/\/$/, "")}${
      ApiRoutes.AI_REPORTING_COPILOT
    }`;
    void fetch(url, {
      method: "PUT",
      headers: {
        ...getHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user_role: rails, prompt }),
    })
      .then(async (res) => {
        if (!res.ok) {
          let detail = "Could not save copilot";
          try {
            const errBody = await res.json();
            detail = parseFastApiDetailBody(errBody);
          } catch {
            /* ignore */
          }
          onError(detail);
          return;
        }
        onSuccess();
      })
      .catch(() => onError("Could not save copilot"));
  }

}
